import { FileExcelOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { Button, DatePicker, Empty, Result, Skeleton, Space, Typography, message } from "antd";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import AppContent from "../../common/AppContent";
import AppPageHeader from "../../common/PageHeader";
import {
  deleteInspection,
  exportFabricInspections,
  fetchAllDefectCategories,
  fetchInspectionById,
  fetchInspectionList,
  isRequestCancelled,
} from "../../apiServices/fabricInspectionService";
import {
  FABRIC_INSPECTION_CREATE_PERMISSION,
  FABRIC_INSPECTION_DELETE_PERMISSION,
  FABRIC_INSPECTION_EDIT_PERMISSION,
} from "../../routes/permissions";
import { hasPermission } from "../../utils/functions";
import { DATE_FORMAT, DEFAULT_LIST_PAGE_SIZE } from "./constants";
import FabricInspectionModal from "./FabricInspectionModal";
import InspectionReportTable from "./InspectionReportTable";
import "./fabricInspection.scss";

const { Title } = Typography;

/**
 * The list endpoint returns only `details_count`, not the defect rows; load them from `show`
 * for rows that have defects so the table still renders server data.
 * @param {import("./types").InspectionRecord[]} rows
 * @param {AbortSignal} signal
 * @returns {Promise<import("./types").InspectionRecord[]>}
 */
const withDetails = async (rows, signal) => {
  const needsDetails = (row) =>
    !Array.isArray(row.inspection_details) && !Array.isArray(row.details) && Number(row.details_count) > 0;
  if (!rows.some(needsDetails)) return rows;

  const results = await Promise.allSettled(
    rows.map((row) => (needsDetails(row) ? fetchInspectionById(row.id, signal) : Promise.resolve(row))),
  );
  if (signal.aborted) return rows;
  return rows.map((row, index) => {
    const result = results[index];
    return result.status === "fulfilled" ? { ...row, details: result.value?.details || [] } : row;
  });
};

const FabricInspection = () => {
  const canCreate = hasPermission([FABRIC_INSPECTION_CREATE_PERMISSION]);
  const canEdit = hasPermission([FABRIC_INSPECTION_EDIT_PERMISSION]);
  const canDelete = hasPermission([FABRIC_INSPECTION_DELETE_PERMISSION]);

  const [month, setMonth] = useState(() => moment().startOf("month"));
  const [reloadKey, setReloadKey] = useState(0);

  const [rows, setRows] = useState(/** @type {import("./types").InspectionRecord[]} */ ([]));
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(/** @type {string|null} */ (null));

  const [defectCategories, setDefectCategories] = useState(/** @type {import("./types").DefectCategory[]} */ ([]));
  const [defectCategoriesLoading, setDefectCategoriesLoading] = useState(true);

  const [modal, setModal] = useState(/** @type {{open:boolean, editId:number|null, key:number}} */ ({
    open: false,
    editId: null,
    key: 0,
  }));

  useEffect(() => {
    const controller = new AbortController();
    fetchAllDefectCategories(controller.signal)
      .then(setDefectCategories)
      .catch((err) => {
        if (!isRequestCancelled(err)) message.error(err?.message || "Failed to load defect categories.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setDefectCategoriesLoading(false);
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await fetchInspectionList(
          {
            perPage: 10000,
            dateFrom: month.clone().startOf("month").format(DATE_FORMAT),
            dateTo: month.clone().endOf("month").format(DATE_FORMAT),
          },
          controller.signal,
        );
        if (controller.signal.aborted) return;
        const hydrated = await withDetails(result.rows, controller.signal);
        if (controller.signal.aborted) return;
        setRows(hydrated);
        setLoadedOnce(true);
      } catch (err) {
        if (isRequestCancelled(err) || controller.signal.aborted) return;
        setError(err?.message || "Failed to load fabric inspections.");
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();

    return () => controller.abort();
  }, [month, reloadKey]);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  const openCreate = () => setModal((m) => ({ open: true, editId: null, key: m.key + 1 }));
  const openEdit = useCallback(
    (record) => setModal((m) => ({ open: true, editId: record.id, key: m.key + 1 })),
    [],
  );
  const closeModal = () => setModal((m) => ({ ...m, open: false, editId: null }));

  const handleSaved = (successMessage) => {
    closeModal();
    message.success(successMessage || "Fabric inspection saved.");
    refetch();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const filters = {};
      if (month) {
        filters.dateFrom = month.clone().startOf("month").format(DATE_FORMAT);
        filters.dateTo = month.clone().endOf("month").format(DATE_FORMAT);
      }
      const result = await exportFabricInspections(filters);
      if (result.ok) {
        message.success(result.message || "Fabric inspection report exported successfully.");
      } else {
        message.error(result.message);
      }
    } catch (err) {
      message.error(err?.message || "Failed to export fabric inspection report.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = useCallback(
    async (record) => {
      const result = await deleteInspection(record.id);
      if (result.ok) {
        message.success(result.message || "Fabric inspection deleted.");
      } else {
        message.error(result.message);
      }
      refetch();
    },
    [refetch],
  );

  const handleMonthChange = (value) => {
    setMonth((value || moment()).clone().startOf("month"));
  };

  const renderContent = () => {
    if (error) {
      return (
        <Result
          status="error"
          title="Could not load fabric inspections"
          subTitle={error}
          extra={
            <Button icon={<ReloadOutlined />} onClick={refetch}>
              Retry
            </Button>
          }
        />
      );
    }
    if (!loadedOnce && loading) {
      return <Skeleton active paragraph={{ rows: 8 }} />;
    }
    if (!loading && rows.length === 0) {
      return (
        <Empty description={`No inspections for ${month.format("MMMM YYYY")}`}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!canCreate}>
            Add Row
          </Button>
        </Empty>
      );
    }
    return (
      <InspectionReportTable
        rows={rows}
        defectCategories={defectCategories}
        loading={loading}
        pagination={false}
        onEdit={openEdit}
        onDelete={handleDelete}
        canEdit={canEdit}
        canDelete={canDelete}
      />
    );
  };

  return (
    <>
      <AppContent
        header={
          <AppPageHeader
            title={
              <Title level={5} style={{ margin: 0 }}>
                Fabric Inspection Report Summary
              </Title>
            }
            extra={
              <Space>
                <DatePicker
                  picker="month"
                  size="small"
                  allowClear={false}
                  value={month}
                  onChange={handleMonthChange}
                  format="MMM YYYY"
                />
                <Button size="small" icon={<ReloadOutlined />} onClick={refetch} disabled={loading}>
                  Refresh
                </Button>
                <Button
                  size="small"
                  icon={<FileExcelOutlined />}
                  onClick={handleExport}
                  loading={exporting}
                  style={{
                    backgroundColor: "#217346",
                    borderColor: "#217346",
                    color: "#ffffff",
                  }}
                >
                  Export Excel
                </Button>
                <Button size="small" type="primary" icon={<PlusOutlined />} onClick={openCreate} disabled={!canCreate}>
                  Add Row
                </Button>
              </Space>
            }
          />
        }
        content={<div style={{ marginTop: 10 }}>{renderContent()}</div>}
      />

      {modal.open && (
        <FabricInspectionModal
          key={modal.key}
          editId={modal.editId}
          defectCategories={defectCategories}
          defectCategoriesLoading={defectCategoriesLoading}
          onCancel={closeModal}
          onSaved={handleSaved}
        />
      )}
    </>
  );
};

export default FabricInspection;
